import { ApiError, api } from '@/shared/api/client';

import { applyAppBadge } from './app-badge';
import { captureAuthLifetime, isAuthLifetimeCurrent } from '@/shared/stores/auth.store';

/**
 * Whose pushes this browser receives (W6 private reminders and rituals carry
 * task titles and today counts — §2.2 P6).
 *
 * A browser holds ONE push subscription per origin, but people share
 * browsers. The server row decides who gets the pushes, so:
 *
 *   - `claimPushDevice` — on every start while signed in (`PushDeviceSync`)
 *     and before "Turn on": re-POST this browser's subscription for me. The
 *     server answers 201 when it is (or now is) mine, or 409
 *     `push_endpoint_taken` when it's still someone else's (they never signed
 *     out here) — then this browser drops that subscription, so their pushes
 *     stop reaching it, and I see "Turn on" instead of a false "On".
 *   - `releasePushDevice` — on sign-out, BEFORE the session ends: forget the
 *     device on the server and unsubscribe the browser.
 *
 * Every step is best-effort and time-boxed: sign-out never hangs, and
 * "Checking this device…" never spins on a slow network.
 */

export type PushDeviceOwnership =
  | 'mine' // the server has this endpoint for me
  | 'released' // it was someone else's: unsubscribed in this browser
  | 'unknown'; // couldn't tell (offline, server error) — nothing changed

const STEP_TIMEOUT_MS = 1_500;
const CLAIM_TIMEOUT_MS = 8_000;

/** One claim per (user, endpoint) — the shell sync and the settings row share it. */
const claims = new Map<string, Promise<PushDeviceOwnership>>();

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | 'timeout'> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve('timeout'), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

function pushApiAvailable(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
}

/** This browser's push subscription, or null (none, unsupported, or no answer in time). */
export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushApiAvailable()) return null;
  try {
    // getRegistration, not `.ready`: `.ready` never settles without a worker.
    const reg = await withTimeout(navigator.serviceWorker.getRegistration('/'), STEP_TIMEOUT_MS);
    if (!reg || reg === 'timeout') return null;
    const sub = await withTimeout(reg.pushManager.getSubscription(), STEP_TIMEOUT_MS);
    return sub === 'timeout' ? null : sub;
  } catch {
    return null;
  }
}

function payloadOf(sub: PushSubscription): { endpoint: string; keys: { p256dh: string; auth: string } } | null {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh, auth } };
}

async function claim(sub: PushSubscription): Promise<PushDeviceOwnership> {
  const payload = payloadOf(sub);
  if (!payload) return 'unknown';
  try {
    const res = await withTimeout(
      api('/api/me/push/subscribe', { method: 'POST', body: payload }),
      CLAIM_TIMEOUT_MS,
    );
    return res === 'timeout' ? 'unknown' : 'mine';
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && err.code === 'push_endpoint_taken') {
      try {
        await sub.unsubscribe();
      } catch {
        // The browser refused; the next start tries again.
      }
      void applyAppBadge(0); // their count must not stay on my icon
      return 'released';
    }
    return 'unknown';
  }
}

/** Make sure this browser's subscription is mine (see the file comment). */
export function claimPushDevice(userId: string, sub: PushSubscription): Promise<PushDeviceOwnership> {
  const key = `${userId}|${sub.endpoint}`;
  const known = claims.get(key);
  if (known) return known;
  const pending = claim(sub).then((r) => {
    if (r === 'unknown') claims.delete(key); // ask again next time
    return r;
  });
  claims.set(key, pending);
  return pending;
}

/** After my own "Turn on" succeeded for `endpoint`. */
export function markPushDeviceMine(userId: string, endpoint: string): void {
  claims.set(`${userId}|${endpoint}`, Promise.resolve('mine'));
}

/** After "Turn off" for `endpoint` (any user). */
export function forgetPushDevice(endpoint: string): void {
  for (const key of claims.keys()) if (key.endsWith(`|${endpoint}`)) claims.delete(key);
}

/**
 * Sign-out: stop this browser receiving my pushes. Call while the session is
 * still valid (the server call needs it). Never throws; bounded in time.
 */
export async function releasePushDevice(): Promise<void> {
  const lifetime = captureAuthLifetime();
  try {
    const sub = await currentPushSubscription();
    if (!sub || !isAuthLifetimeCurrent(lifetime)) return;
    await withTimeout(
      api('/api/me/push/unsubscribe-by-endpoint', {
        method: 'POST',
        body: { endpoint: sub.endpoint },
      }).catch(() => undefined),
      STEP_TIMEOUT_MS,
    );
    if (!isAuthLifetimeCurrent(lifetime)) return;
    // The local unsubscribe is what stops delivery: the push service then
    // answers 410 and the server prunes any row that is left.
    await withTimeout(
      sub.unsubscribe().catch(() => false),
      STEP_TIMEOUT_MS,
    );
    forgetPushDevice(sub.endpoint);
  } catch {
    // Best-effort: sign-out goes on regardless.
  } finally {
    if (isAuthLifetimeCurrent(lifetime)) void applyAppBadge(0);
  }
}

/** Test seam. */
export function resetPushDeviceClaims(): void {
  claims.clear();
}
